import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import {
  AP_INVOICE_IGNORED_TASK_OUTCOME_REASON,
  SystemEntity,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';

export function invoiceHasTasksClosedForIgnore(
  tasks: ReadonlyArray<{ outcomeReason: string | null }>,
): boolean {
  return tasks.some(
    (task) => task.outcomeReason === AP_INVOICE_IGNORED_TASK_OUTCOME_REASON,
  );
}

/**
 * Whether this invoice has tasks that were cancelled because it was ignored.
 * The list is filtered by that outcome reason before the page limit, so an
 * older ignore-closed task is not hidden by unrelated cancellations.
 * Callers who cannot read tasks get false; unignore still reopens those tasks
 * unless the caller opts out.
 */
export function useIgnoredInvoiceTasks(invoiceId: string) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    ...orpc.tasks.list.queryOptions({
      input: {
        anchorEntityType: SystemEntity.AP_INVOICE,
        anchorEntityId: invoiceId,
        statuses: ['cancelled'],
        outcomeReason: AP_INVOICE_IGNORED_TASK_OUTCOME_REASON,
        limit: 100,
        offset: 0,
      },
    }),
    enabled: canReadTasks,
  });

  return {
    hasClosedForIgnore: invoiceHasTasksClosedForIgnore(query.data?.tasks ?? []),
    isLoading: canReadTasks && query.isLoading,
  };
}
